import classcat from "classcat";
import React, { SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import { IconExclamationCircle } from "@touchtech/web-baselayer-ui";

import { Image } from "@is/components/Image";
import { MediaVariants, TranslationContext } from "@is/constants";
import { BasketItem as BasketItemType } from "@is/types";
import { convertCurrencyCodeToSymbol } from "@is/utils";

import css from "./BasketItemTemplate.module.css";

const WARNING_MESSAGE_LIMIT = 73;

type Props = {
  item: BasketItemType;
  onClick: () => void;
  onDecreaseQuantity: (event: SyntheticEvent) => void;
  onIncreaseQuantity: (event: SyntheticEvent) => void;
  isLimitationWarningShown: boolean;
  warningText: string;
};

const BasketItemTemplate = ({
  onClick,
  item,
  onDecreaseQuantity,
  onIncreaseQuantity,
  isLimitationWarningShown,
  warningText,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Basket);

  return (
    <div className={css.container}>
      <div className={css.productInfoContainer} onClick={onClick}>
        {!!item.media?.url && (
          <div className={css.imageContainer}>
            <Image
              src={item.media?.url}
              mediavariant={MediaVariants.MEDIUM}
              layout="cover"
            />
          </div>
        )}
        <div
          className={classcat(["text-label-base", css.styleDetailsContainer])}
        >
          <div>
            <div
              className={classcat([css.styleNameContainer, "text-label-xl"])}
            >
              <div>{item.productName}</div>
            </div>
            <div className={css.productInfo}>
              <div>
                <span className={css.styleDetailsLabel}>{t("color")}</span>
                {item.colorName}
              </div>
              {item?.size && (
                <div>
                  <span className={css.styleDetailsLabel}>{t("size")}</span>
                  {item.size}
                </div>
              )}
              {item?.length && (
                <div>
                  <span className={css.styleDetailsLabel}>{t("length")}</span>
                  {item.length}
                </div>
              )}
            </div>
          </div>
          {!!(item.price?.retailPrice && item.price?.currencyCode) && (
            <div className={css.price}>
              <strong>
                {`${
                  item.price?.salePrice || item.price.retailPrice
                } ${convertCurrencyCodeToSymbol(item.price.currencyCode)}`}
              </strong>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className={css.counterWrapper}>
          <div className={css.counter}>
            <button
              className="text-body-base"
              onClick={onDecreaseQuantity}
              disabled={item.quantity === 0}
            >
              -
            </button>
            <div className={classcat(["text-label-base", css.quantityCounter])}>
              {item.quantity}
            </div>
            <button
              className="text-body-base"
              onClick={onIncreaseQuantity}
              disabled={item.quantity === item.onlineQuantity}
            >
              +
            </button>
          </div>
          {isLimitationWarningShown && (
            <div
              className={classcat([css.warningContainer, "text-label-base"])}
            >
              <div>
                <IconExclamationCircle />
              </div>
              <span
                className={classcat([
                  css.warningText,
                  "text-label-base",
                  {
                    ["text-label-sm"]:
                      warningText?.length >= WARNING_MESSAGE_LIMIT,
                  },
                ])}
              >
                {warningText}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasketItemTemplate;
